// frontend/components/sidebar/TrashModal.tsx

"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import {
  Search, X, Trash, FileText, RotateCcw, Trash2,
  ChevronDown, User, FolderSearch, Check, Loader2,
} from "lucide-react";
import * as Lucide from "lucide-react";
import { useTrashActions } from "../../hooks/useTrashActions";
import { useSidebarContext } from "../../contexts/SidebarContext";
import { API_URL } from "../../utils/api";
import { getTwemojiSvgUrl } from "../../utils/emoji";

interface TrashModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TrashModal({ isOpen, onClose }: TrashModalProps) {
  const { data: session } = useSession();
  const user: any = session?.user;
  const router = useRouter();
  const { docs: allDocs } = useSidebarContext();

  const { trashedDocs, isLoading, fetchTrash, handleRestore, handleHardDelete } = useTrashActions(user, API_URL);

  const [searchQuery, setSearchQuery]       = useState("");
  const [editorFilter, setEditorFilter]     = useState<string | null>(null);
  const [scopeFilter, setScopeFilter]       = useState<{ id: string; title: string } | null>(null);

  const [editorOpen, setEditorOpen]         = useState(false);
  const [scopeOpen, setScopeOpen]           = useState(false);
  const [scopeSearch, setScopeSearch]       = useState("");

  const editorRef = useRef<HTMLDivElement>(null);
  const scopeRef  = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) { fetchTrash(); setSearchQuery(""); setEditorFilter(null); setScopeFilter(null); }
  }, [isOpen]);

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(e.target as Node)) setEditorOpen(false);
      if (scopeRef.current  && !scopeRef.current.contains(e.target as Node))  setScopeOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // 최종 편집자 목록 (중복 제거)
  const editors = useMemo(
    () => [...new Set(trashedDocs.map(d => d.updatedBy).filter(Boolean))] as string[],
    [trashedDocs]
  );

  // 검색 범위 후보 (비삭제 문서에서 검색)
  const scopeCandidates = useMemo(() => {
    if (!scopeSearch.trim()) return allDocs.slice(0, 20);
    return allDocs.filter((d: any) => d.title?.toLowerCase().includes(scopeSearch.toLowerCase())).slice(0, 20);
  }, [allDocs, scopeSearch]);

  // 상위 문서 추적용 맵 — breadcrumb(상위 경로) 계산에 사용
  const docMap = useMemo(() => {
    const map = new Map<string, any>();
    allDocs.forEach((d: any) => map.set(d.id, d));
    return map;
  }, [allDocs]);

  // 후보 문서가 실제로 어느 경로(상위 문서 체인) 아래에 있는지 보여주는 breadcrumb 계산
  const buildBreadcrumb = (doc: any): string => {
    const parts: string[] = [];
    let cur = doc.parentId ? docMap.get(doc.parentId) : null;
    let depth = 0;
    while (cur && depth < 6) {
      parts.unshift(cur.title || "새 페이지");
      cur = cur.parentId ? docMap.get(cur.parentId) : null;
      depth++;
    }
    if (parts.length === 0) {
      return doc.workspaceType === "PERSONAL" ? "개인 스페이스" : "팀 스페이스";
    }
    return parts.join(" / ");
  };

  // 최종 필터링
  const filtered = useMemo(() => trashedDocs.filter(doc => {
    if (searchQuery && !doc.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (editorFilter && doc.updatedBy !== editorFilter) return false;
    if (scopeFilter  && doc.parentId  !== scopeFilter.id) return false;
    return true;
  }), [trashedDocs, searchQuery, editorFilter, scopeFilter]);

  if (!isOpen) return null;

  const renderIcon = (iconString: string | null | undefined) => {
    if (!iconString) return <FileText size={14} className="text-gray-400 dark:text-zinc-500" />;
    if (iconString.startsWith("emoji:")) {
      const code = iconString.replace("emoji:", "");
      if (/^[0-9a-f\-]+$/.test(code))
        return <img src={getTwemojiSvgUrl(code)} alt="" className="w-3.5 h-3.5 object-contain" />;
      return <span className="text-sm leading-none">{code}</span>;
    }
    if (iconString.startsWith("lucide:")) {
      const parts = iconString.replace("lucide:", "").split(":");
      const Icon = (Lucide as any)[parts[0]];
      if (Icon) return <Icon size={14} color={parts[1] !== "currentColor" ? parts[1] : undefined} />;
    }
    if (iconString.startsWith("upload:"))
      return <img src={iconString.replace("upload:", "")} alt="" className="w-3.5 h-3.5 object-cover rounded-sm" />;
    return <FileText size={14} className="text-gray-400 dark:text-zinc-500" />;
  };

  const chipCls = (active: boolean) =>
    `flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer select-none ${
      active
        ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400"
        : "bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-zinc-400 hover:border-gray-300 dark:hover:border-zinc-600"
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-900 w-130 max-h-[75vh] rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-zinc-800"
        onClick={e => e.stopPropagation()}
      >
        {/* 검색 바 */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-zinc-800">
          <Search size={16} className="text-gray-400 dark:text-zinc-500 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="휴지통에서 페이지 검색..."
            className="flex-1 outline-none text-sm bg-transparent placeholder-gray-400 dark:placeholder-zinc-500 text-gray-800 dark:text-zinc-100"
            autoFocus
          />
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded text-gray-400 dark:text-zinc-500">
            <X size={16} />
          </button>
        </div>

        {/* 필터 칩 */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-zinc-800">
          {/* 최종 편집자 */}
          <div className="relative" ref={editorRef}>
            <button onClick={() => { setEditorOpen(v => !v); setScopeOpen(false); }} className={chipCls(!!editorFilter)}>
              <User size={11} />
              {editorFilter ? editorFilter : "최종 편집자"}
              {editorFilter
                ? <X size={10} className="ml-0.5" onClick={e => { e.stopPropagation(); setEditorFilter(null); }} />
                : <ChevronDown size={11} />
              }
            </button>
            {editorOpen && (
              <div className="absolute top-full left-0 mt-1 z-10 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-xl py-1 min-w-36">
                {editors.length === 0
                  ? <p className="px-3 py-2 text-xs text-gray-400 dark:text-zinc-500">편집자 없음</p>
                  : editors.map(e => (
                    <button
                      key={e}
                      onClick={() => { setEditorFilter(editorFilter === e ? null : e); setEditorOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                    >
                      {editorFilter === e && <Check size={11} className="text-blue-500 shrink-0" />}
                      <span className={editorFilter === e ? "font-medium" : ""}>{e}</span>
                    </button>
                  ))
                }
              </div>
            )}
          </div>

          {/* 검색 범위 */}
          <div className="relative" ref={scopeRef}>
            <button onClick={() => { setScopeOpen(v => !v); setEditorOpen(false); }} className={chipCls(!!scopeFilter)}>
              <FolderSearch size={11} />
              {scopeFilter ? scopeFilter.title : "검색 범위:"}
              {scopeFilter
                ? <X size={10} className="ml-0.5" onClick={e => { e.stopPropagation(); setScopeFilter(null); setScopeSearch(""); }} />
                : <ChevronDown size={11} />
              }
            </button>
            {scopeOpen && (
              <div className="absolute top-full left-0 mt-1 z-10 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-xl w-56">
                <div className="p-2 border-b border-gray-100 dark:border-zinc-700">
                  <input
                    autoFocus
                    type="text"
                    value={scopeSearch}
                    onChange={e => setScopeSearch(e.target.value)}
                    placeholder="페이지 검색하기"
                    className="w-full text-xs px-2 py-1.5 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded outline-none placeholder-gray-400 dark:placeholder-zinc-500 text-gray-800 dark:text-zinc-100"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto py-1">
                  {scopeCandidates.length === 0
                    ? <p className="px-3 py-2 text-xs text-gray-400 dark:text-zinc-500">결과 없음</p>
                    : scopeCandidates.map((d: any) => (
                      <button
                        key={d.id}
                        onClick={() => { setScopeFilter({ id: d.id, title: d.title || "새 페이지" }); setScopeOpen(false); setScopeSearch(""); }}
                        className="w-full flex flex-col items-start px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                      >
                        <span className="font-medium text-gray-800 dark:text-zinc-100 truncate w-full">{d.title || "새 페이지"}</span>
                        <span className="text-gray-400 dark:text-zinc-500 truncate w-full">{buildBreadcrumb(d)}</span>
                      </button>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 문서 목록 */}
        <div className="flex-1 overflow-y-auto min-h-52">
          {isLoading ? (
            <div className="h-full flex items-center justify-center py-12 text-gray-400 dark:text-zinc-500">
              <Loader2 size={20} className="animate-spin mr-2" />
              <span className="text-sm">불러오는 중...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center py-12 text-gray-400 dark:text-zinc-500 gap-3">
              <Trash size={28} strokeWidth={1.5} />
              <p className="text-sm font-medium">표시할 결과 없음</p>
            </div>
          ) : (
            filtered.map(doc => (
              <div
                key={doc.id}
                className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors group"
              >
                {/* 문서 정보 */}
                <button
                  className="flex items-start gap-2.5 flex-1 min-w-0 text-left"
                  onClick={() => { router.push(`/doc/${doc.id}`); onClose(); }}
                >
                  <div className="w-4 flex items-center justify-center shrink-0 mt-0.5">{renderIcon(doc.icon)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 dark:text-zinc-100 truncate font-medium">{doc.title || "새 페이지"}</p>
                    {doc.updatedBy && (
                      <p className="text-xs text-gray-400 dark:text-zinc-500 truncate">{doc.updatedBy}</p>
                    )}
                  </div>
                </button>

                {/* 복구 / 영구삭제 버튼 */}
                <div className="flex items-center gap-1 ml-2 shrink-0">
                  <button
                    onClick={e => handleRestore(doc.id, e)}
                    title="복구하기"
                    className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-400 dark:text-zinc-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                  >
                    <RotateCcw size={14} />
                  </button>
                  <button
                    onClick={e => handleHardDelete(doc.id, e)}
                    title="영구 삭제"
                    className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 하단 안내 */}
        <div className="px-4 py-2.5 border-t border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900/50">
          <p className="text-xs text-gray-400 dark:text-zinc-500 text-center">
            페이지는 휴지통에 30일 동안 보관되며 자동 삭제가 예정되어 있습니다.
          </p>
        </div>
      </div>
    </div>
  );
}
